/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Attribute, ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChildren, ElementRef, EventEmitter, forwardRef, Injector, Input, Output, QueryList, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';
import { asyncScheduler, BehaviorSubject, Observable } from 'rxjs';
import { observeOn } from 'rxjs/operators';
import { CompareWithFn } from '../../+utils/compare-with-fn';
import { DisplayWithFn } from '../../+utils/display-with-fn';
import { FormFieldControl } from '../../+legacy/form/form-field/form-field-control';
import { OptNode } from '../option/opt-node';
import { OptionProviders } from '../option/option-providers';
import { OptionsOverlayComponent } from '../option/options-overlay/options-overlay.component';
import { OptionsRoot } from '../option/options-root';

@Component({
    selector: 'ngf-select,ngf-select[small]',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        OptionProviders(SelectComponent),
        {
            provide: FormFieldControl,
            useExisting: SelectComponent
        },
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectComponent),
            multi: true
        }
    ],
    standalone: false
})
export class SelectComponent implements ControlValueAccessor, FormFieldControl, OptionsRoot {

  @Input()
  set selected(value: any) {
    this.writtenValue.next(value);
  }
  @Input()
  disabled = false;

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('readonly')
  isReadonly = false;

  @Input()
  placeholder = '';
  @Input()
  hasError = false;
  @Input()
  hasShadow = false;
  @Input()
  multiselect = false;
  @Input()
  legacyMultiselect = false;
  @Input()
  clearable = false;

  @Input()
  icon = null;
  @Input()
  prefixIcon = null;

  /**
   * customize display of selected value,
   * by default option's HTMLElement.textContent will be displayed
   */
  @Input()
  displayWith: DisplayWithFn<any> | null = null;

  /**
   * customize value comparison,
   * by default values will be compared by reference
   */
  @Input()
  compareWith: CompareWithFn<any> | null = null;

  /**
   * allow null as option value,
   * by default null is treated as no value
   */
  @Input()
  nullAsValue = false;

  @Input()
  showOverflowTooltip = false;

  @Input()
  ariaLabel?: string;

  @Input()
  ariaLabelledby?: string;

  @Input()
  ariaDescribedby?: string;

  @Output()
  selectedChange = new EventEmitter();

  @ViewChild(OptionsOverlayComponent, { static: true })
  optionsOverlay: OptionsOverlayComponent;

  @ContentChildren(OptNode, { descendants: true })
  allChildren: QueryList<OptNode>;

  constructor(private cdr: ChangeDetectorRef,
              @Attribute('small') public small: any,
              public elementRef: ElementRef<HTMLElement>,
              private injector: Injector) {
    this.writtenValue = new BehaviorSubject<any>(this.multiselect ? [] : null);
    this.writtenValue$ = this.writtenValue.pipe(observeOn(asyncScheduler));
  }

  private readonly writtenValue: BehaviorSubject<any>;

  readonly writtenValue$: Observable<any>;

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  get children(): OptNode[] {
    // OptNode's creates a tree, but @ContentChildren(..., {descendants: true}) return flattened array of all nodes
    // because of that we need only direct children, aka without `parent`
    return this.allChildren.filter(node => !node.parent);
  }

  handleSelection(value: any) {
    this.selectedChange.emit(value);
    this.onChange(value);
    this.onTouched();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  writeValue(value: any): void {
    this.writtenValue.next(value);
    this.cdr.markForCheck();
  }

  setHasError(hasError: boolean): void {
    this.hasError = hasError;
    this.cdr.markForCheck();
  }

  get isRequired(): boolean {
    const ngControl = this.injector.get(NgControl, null, { optional: true, self: true });
    if (!ngControl?.control) return false;

    return ngControl.control.hasValidator(Validators.required);
  }

}
